// assert-mattermost-url-safe.ts
//
// Hardening (P2 A3): validate Mattermost base URL TRƯỚC khi gửi Bearer bot token.
// Bot token là secret → chặn (1) TLS downgrade (http:// tới host non-local →
// token cleartext sniff được) + (2) gửi token nhầm host lạ (typo/config sai).
//
// URL đến từ `--mattermost-url` (user nhập) hoặc DEFAULT_MM_URL → defense-in-depth
// + fail-safe cho lỗi cấu hình, KHÔNG phải SSRF remote.
//
// Chính sách (Luke chốt D1 260716):
//   - host non-local (mm.nal.vn) → CHỈ https (http → reject).
//   - host localhost / 127.0.0.1 → cho cả http + https (dev local thường HTTP;
//     loopback không qua mạng nên cleartext không sniff được từ xa).
//   - host khác → reject (ngoài allowlist).
//
// Host-allowlist DELEGATE cho `assertAllowedEndpointHost` (đã tested + lowercase
// host) để tránh DRY-drift với các gate credentials khác. Helper này CHỈ lo
// parse + scheme-enforcement.
import { assertAllowedEndpointHost } from "./validate-endpoint-host-allowlist";

const ALLOWED_HOSTS = ["mm.nal.vn", "localhost", "127.0.0.1"];
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1"]);

// Throw Error (message VN) nếu url không hợp lệ. KHÔNG log token (chỉ host/scheme).
export function assertMattermostUrlSafe(url: string): void {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error(
      `URL Mattermost không hợp lệ: "${url}" không parse được. ` +
        `Chỉ chấp nhận https://mm.nal.vn (hoặc http/https://localhost cho dev).`,
    );
  }

  const host = parsed.hostname.toLowerCase();
  const isLocal = LOCAL_HOSTS.has(host);

  // Scheme: host non-local BẮT BUỘC https (chặn TLS downgrade cleartext token).
  // Local cho cả http + https.
  if (!isLocal && parsed.protocol !== "https:") {
    throw new Error(
      `URL Mattermost phải dùng https cho host non-local: "${parsed.protocol}//${host}". ` +
        `Từ chối gửi bot token qua kết nối không mã hoá.`,
    );
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new Error(
      `URL Mattermost scheme không hợp lệ: "${parsed.protocol}". Chỉ chấp nhận http/https.`,
    );
  }

  // Host-allowlist (DELEGATE — exact-match + lowercase). exactHosts gồm cả local
  // + mm.nal.vn; scheme của local đã cho qua ở trên, non-local đã ép https.
  assertAllowedEndpointHost(
    host,
    { exactHosts: ALLOWED_HOSTS },
    (m) => new Error(`URL Mattermost: ${m}`),
  );
}
